import { http } from '@/lib/http';
import type {
  DispatchLabel,
  DispatchLabelListRow,
  DispatchLabelPrintable,
  DispatchLabelStatus,
  DispatchOrder,
  DispatchPartner,
  DispatchStatus,
} from '@/types/dispatch';

export type { DispatchPartner } from '@/types/dispatch';

const BASE = '/api/v1/dispatch';

// ── Dispatcher console (routing brain) ─────────────────────────────────

export interface DispatcherTierConfig {
  tier: string;
  enabled: boolean;
  label: string;
  fulfilmentMode: string;
  fixedPartnerId: string | null;
  fixedPartner: string | null;
  optimise: string;
  maxEtaDays: number | null;
  pricingMode: string;
}

export interface DispatcherRecentRow {
  id: string;
  dispatchNumber: string;
  createdAt: string;
  status: string;
  destination: string;
  orderNumber: string | null;
  tier: string | null;
  method: string | null;
  courierName: string | null;
  partnerCode: string | null;
  etaDays: number | null;
  rate: number | null;
  reason: string | null;
}

export interface CarrierScorecard {
  partnerCode: string;
  courierName: string | null;
  shipments: number;
  delivered: number;
  rto: number;
  failed: number;
  onTimePct: number | null;
  avgTransitDays: number | null;
  rtoPct: number;
  avgRate: number | null;
}

export interface DispatcherInsights {
  windowDays: number;
  summary: {
    total: number;
    byMethod: Record<string, number>;
    byTier: Record<string, number>;
  };
  config: {
    tiers: DispatcherTierConfig[];
    partners: Array<{
      id: string;
      code: string;
      name: string;
      provider: string;
      connectionStatus: string;
    }>;
    ruleCount: number;
  };
  recent: DispatcherRecentRow[];
  performance: CarrierScorecard[];
  recommendations: RoutingRecommendation[];
}

export interface RoutingRecommendation {
  id: string;
  severity: 'WARN' | 'SUGGEST' | 'INFO';
  title: string;
  detail: string;
  partnerCode?: string;
  courierName?: string | null;
}

// ── Dispatch orders ────────────────────────────────────────────────────

export interface DispatchListQuery {
  status?: DispatchStatus;
  salesOrderId?: string;
  partnerId?: string;
  trackingNumber?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface DispatchListResponse {
  rows: DispatchOrder[];
  page: number;
  pageSize: number;
  total: number;
}

export interface CreateDispatchPayload {
  salesOrderId: string;
  partnerId?: string;
  trackingNumber?: string;
  externalDispatchRef?: string;
  expectedDeliveryAt?: string;
  shippingAddressLine1?: string;
  shippingAddressLine2?: string;
  shippingCity?: string;
  shippingState?: string;
  shippingPostalCode?: string;
  shippingCountry?: string;
  shippingContactName?: string;
  shippingContactPhone?: string;
  notes?: string;
}

export interface UpdateDispatchPayload {
  partnerId?: string | null;
  trackingNumber?: string;
  externalDispatchRef?: string;
  expectedDeliveryAt?: string;
  shippingAddressLine1?: string;
  shippingAddressLine2?: string;
  shippingCity?: string;
  shippingState?: string;
  shippingPostalCode?: string;
  shippingCountry?: string;
  shippingContactName?: string;
  shippingContactPhone?: string;
  notes?: string;
}

export interface UpdateDispatchStatusPayload {
  status: DispatchStatus;
  reason?: string;
  location?: string;
  occurredAt?: string;
  /** Sh-scanverify — bypass "all items scanned" gate on PENDING → READY_FOR_PICKUP. */
  forcePartial?: boolean;
}

/** Sh-scanverify — one item being scanned into the outbound package. */
export interface ScanDispatchItemPayload {
  sku: string;
  /** Defaults to 1 server-side. */
  quantity?: number;
}

/** Sh-scanverify — reset / decrement scanned count on a specific item. */
export interface UnscanDispatchItemPayload {
  itemId: string;
  /** Omit to reset to 0. */
  quantity?: number;
}

// ── Labels ─────────────────────────────────────────────────────────────

export interface CreateDispatchLabelPayload {
  weightGrams?: number;
  lengthCm?: number;
  widthCm?: number;
  heightCm?: number;
  pieces?: number;
  packageNotes?: string;
  partnerCode?: string;
  awbNumber?: string;
  externalLabelRef?: string;
}

// ── Courier selection (aggregators) ────────────────────────────────────

/** One serviceable carrier for a dispatch's route, from the partner's
 *  serviceability lookup. The operator picks one before AWB booking. */
export interface ServiceableCourier {
  serviceCode: string;
  serviceLabel: string;
  estimatedDeliveryDays?: number;
  rate?: number;
  courierId?: string;
  courierName?: string;
  etd?: string;
  rating?: number;
  isSurface?: boolean;
  isCod?: boolean;
  isRecommended?: boolean;
}

export interface SelectCourierPayload {
  courierId: string;
  courierName?: string;
  etaDays?: number;
  rate?: number;
}

// ── Partners ───────────────────────────────────────────────────────────

export interface CreateDispatchPartnerPayload {
  code: string;
  name: string;
  /** DEPRECATED — use `provider`. Optional during the provider migration. */
  type?: 'MANUAL' | 'COURIER';
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
}

export const dispatchService = {
  // Orders
  list: (query: DispatchListQuery = {}) =>
    http.get<DispatchListResponse>(`${BASE}/orders`, { params: query }),
  get: (id: string) => http.get<DispatchOrder>(`${BASE}/orders/${id}`),
  create: (payload: CreateDispatchPayload) =>
    http.post<DispatchOrder, CreateDispatchPayload>(`${BASE}/orders`, payload),
  update: (id: string, payload: UpdateDispatchPayload) =>
    http.patch<DispatchOrder, UpdateDispatchPayload>(`${BASE}/orders/${id}`, payload),
  updateStatus: (id: string, payload: UpdateDispatchStatusPayload) =>
    http.patch<DispatchOrder, UpdateDispatchStatusPayload>(
      `${BASE}/orders/${id}/status`,
      payload,
    ),
  // Sh-scanverify — Pack & verify endpoints used by the pack panel
  // on the dispatch detail screen.
  scan: (dispatchId: string, payload: ScanDispatchItemPayload) =>
    http.post<DispatchOrder, ScanDispatchItemPayload>(
      `${BASE}/orders/${dispatchId}/scan`,
      payload,
    ),
  unscan: (dispatchId: string, payload: UnscanDispatchItemPayload) =>
    http.post<DispatchOrder, UnscanDispatchItemPayload>(
      `${BASE}/orders/${dispatchId}/unscan`,
      payload,
    ),
  // Re-attempt the carrier AWB booking after a failed attempt — used
  // by the "Retry AWB" button on the dispatch detail. Fire-and-forget
  // on the BE; the FE re-fetches the dispatch shortly after.
  retryAwb: (dispatchId: string) =>
    http.post<DispatchOrder, undefined>(
      `${BASE}/orders/${dispatchId}/retry-awb`,
      undefined,
    ),

  // Manual / retry — request the courier pickup. Awaited on the BE, so
  // the returned dispatch reflects pickupScheduledAt / pickupError.
  schedulePickup: (dispatchId: string) =>
    http.post<DispatchOrder, undefined>(
      `${BASE}/orders/${dispatchId}/schedule-pickup`,
      undefined,
    ),

  // Reconcile a carrier-tracked dispatch — pulls the live status from
  // the carrier's tracking API. The returned dispatch reflects any
  // status the carrier had that a missed webhook never delivered.
  syncTracking: (dispatchId: string) =>
    http.post<DispatchOrder, undefined>(
      `${BASE}/orders/${dispatchId}/sync-tracking`,
      undefined,
    ),

  // Courier selection (aggregators) — fetch the carriers serving this
  // dispatch's route (rate + SLA), and pin the operator's choice onto
  // the label before the AWB is booked.
  listCouriers: (dispatchId: string) =>
    http.get<ServiceableCourier[]>(`${BASE}/orders/${dispatchId}/couriers`),
  selectCourier: (dispatchId: string, payload: SelectCourierPayload) =>
    http.post<DispatchOrder, SelectCourierPayload>(
      `${BASE}/orders/${dispatchId}/select-courier`,
      payload,
    ),

  // Action a Non-Delivery Report — re-attempt delivery or RTO. The status
  // change arrives later via the carrier's tracking webhook.
  ndrAction: (dispatchId: string, action: 'reattempt' | 'rto') =>
    http.post<{ ok: boolean; message?: string }, { action: 'reattempt' | 'rto' }>(
      `${BASE}/orders/${dispatchId}/ndr`,
      { action },
    ),

  /**
   * MOCK-carrier test rig — POSTs an unsigned tracking webhook to the
   * same `/integrations/tracking/:partnerId/webhook` endpoint a real
   * carrier hits. Behind the FE in operator-mode so an operator can
   * walk a MOCK-partner dispatch through its lifecycle from the
   * detail sheet without curl. The endpoint is intentionally public
   * (the MOCK adapter's verificationMethod is `none`); for real
   * carriers it stays HMAC-signed and unreachable from here.
   */
  simulateCarrierWebhook: (
    partnerId: string,
    body: {
      awb: string;
      status: string;
      location?: string;
      reason?: string;
      occurredAt?: string;
    },
  ) =>
    http.post<{ accepted: true; dispatchOrderId?: string }, typeof body>(
      `/api/v1/integrations/tracking/${partnerId}/webhook`,
      body,
    ),

  // Labels (one per dispatch order, idempotent on dispatchOrderId)
  createLabel: (dispatchId: string, payload: CreateDispatchLabelPayload) =>
    http.post<DispatchLabel, CreateDispatchLabelPayload>(
      `${BASE}/orders/${dispatchId}/label`,
      payload,
    ),
  getLabel: (labelId: string) => http.get<DispatchLabel>(`${BASE}/labels/${labelId}`),
  printLabel: (labelId: string) =>
    http.get<DispatchLabelPrintable>(`${BASE}/labels/${labelId}/print`),
  listLabels: (
    query: {
      status?: DispatchLabelStatus;
      dispatchOrderId?: string;
      awbNumber?: string;
      page?: number;
      pageSize?: number;
    } = {},
  ) =>
    http.get<{ rows: DispatchLabelListRow[]; page: number; pageSize: number; total: number }>(
      `${BASE}/labels`,
      { params: query },
    ),
  updateLabelStatus: (
    labelId: string,
    payload: { status: DispatchLabelStatus; reason?: string },
  ) =>
    http.patch<DispatchLabel, { status: DispatchLabelStatus; reason?: string }>(
      `${BASE}/labels/${labelId}/status`,
      payload,
    ),

  // Dispatcher console — the routing brain
  routingInsights: () => http.get<DispatcherInsights>(`${BASE}/dispatcher/insights`),

  // Partners
  listPartners: () => http.get<DispatchPartner[]>(`${BASE}/partners`),
  getPartner: (id: string) => http.get<DispatchPartner>(`${BASE}/partners/${id}`),
  createPartner: (payload: CreateDispatchPartnerPayload & {
    provider?: import('@/types/dispatch').PartnerProvider;
  }) =>
    http.post<DispatchPartner, typeof payload>(`${BASE}/partners`, payload),
  updatePartner: (
    id: string,
    payload: Partial<CreateDispatchPartnerPayload> & {
      provider?: import('@/types/dispatch').PartnerProvider;
      status?: 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
      pickupWarehouseId?: string | null;
      enabledServices?: import('@/types/dispatch').PartnerService[];
      autoSchedulePickup?: boolean;
      exposedToPortal?: boolean;
      portalDisplayName?: string;
      portalBaseRate?: number;
      portalRates?: Record<string, number>;
      portalEtaDays?: number;
    },
  ) => http.patch<DispatchPartner, typeof payload>(`${BASE}/partners/${id}`, payload),

  /**
   * Phase 1 — delete a partner. BE hard-deletes when no dispatch /
   * routing-rule history exists, otherwise soft-deletes (status ARCHIVED).
   * Response indicates which path was taken so the toast can phrase it
   * correctly.
   */
  deletePartner: (id: string) =>
    http.delete<{ deleted: boolean; archived: boolean }>(`${BASE}/partners/${id}`),

  // Sh-rebuild-B — wizard hooks
  testPartner: (id: string, credentials: Record<string, unknown>) =>
    http.post<
      {
        partner: DispatchPartner;
        testResult: import('@/types/dispatch').TestConnectionResult;
      },
      { credentials: Record<string, unknown> }
    >(`${BASE}/partners/${id}/test`, { credentials }),
  rotatePartnerWebhook: (id: string) =>
    http.post<DispatchPartner, undefined>(`${BASE}/partners/${id}/rotate-webhook`, undefined),

  // Provider specs (drives wizard rendering)
  listProviderSpecs: () =>
    http.get<import('@/types/dispatch').PartnerProviderSpec[]>(`${BASE}/partners/providers`),
  getProviderSpec: (provider: string) =>
    http.get<import('@/types/dispatch').PartnerProviderSpec>(
      `${BASE}/partners/providers/${provider}`,
    ),
};

// ── Assignment Rules (D-2) ─────────────────────────────────────────────

export type DispatchAssignmentRuleStatus = 'ACTIVE' | 'INACTIVE';
export type DispatchRuleScope = 'ALL' | 'DOMESTIC' | 'INTERNATIONAL';

export interface DispatchAssignmentRule {
  id: string;
  tenantId: string;
  name: string;
  priority: number;
  status: DispatchAssignmentRuleStatus;
  /** Destination scope — ALL / DOMESTIC (home country) / INTERNATIONAL. */
  scope: DispatchRuleScope;
  countryIn: string[];
  stateIn: string[];
  warehouseCodeIn: string[];
  /** Tier scope — null = any tier; STANDARD/EXPRESS = only that tier. */
  tier: 'STANDARD' | 'EXPRESS' | null;
  partnerId: string;
  /** Sh-rebuild-E — service within the partner. NULL = partner default. */
  serviceCode: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAssignmentRulePayload {
  name: string;
  priority?: number;
  status?: DispatchAssignmentRuleStatus;
  scope?: DispatchRuleScope;
  countryIn?: string[];
  stateIn?: string[];
  warehouseCodeIn?: string[];
  tier?: 'STANDARD' | 'EXPRESS' | null;
  partnerId: string;
  /** Empty string clears the value on update. Omit on create to leave NULL. */
  serviceCode?: string | null;
}

export type UpdateAssignmentRulePayload = Partial<CreateAssignmentRulePayload>;

export const dispatchRulesService = {
  list: (status?: DispatchAssignmentRuleStatus) =>
    http.get<DispatchAssignmentRule[]>(`${BASE}/assignment-rules`, {
      params: status ? { status } : undefined,
    }),
  get: (id: string) =>
    http.get<DispatchAssignmentRule>(`${BASE}/assignment-rules/${id}`),
  create: (payload: CreateAssignmentRulePayload) =>
    http.post<DispatchAssignmentRule, CreateAssignmentRulePayload>(
      `${BASE}/assignment-rules`,
      payload,
    ),
  update: (id: string, payload: UpdateAssignmentRulePayload) =>
    http.patch<DispatchAssignmentRule, UpdateAssignmentRulePayload>(
      `${BASE}/assignment-rules/${id}`,
      payload,
    ),
  remove: (id: string) =>
    http.delete<void>(`${BASE}/assignment-rules/${id}`),
};

